import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ZoomIn, ZoomOut, RotateCcw, Move, Grid3X3, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ImageViewer = ({ 
  image, 
  corrosionAreas = [], 
  showOverlay,
  label,
  gridSize = 2,
  ...props 
}) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [selectedTile, setSelectedTile] = useState(null);
  const containerRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Parse corrosion areas to determine which tiles have issues
  const getTilesWithCorrosion = () => {
    const tiles = new Set();
    corrosionAreas.forEach(area => {
      // Extract tile info from description like "[tile_0_2] White crystalline..."
      const match = area.description?.match(/\[tile_(\d+)_(\d+)\]/);
      if (match) {
        tiles.add(`${match[1]}_${match[2]}`);
      }
    });
    return tiles;
  };

  const tilesWithCorrosion = getTilesWithCorrosion();

  // Get corrosion details for a specific tile
  const getTileDetails = (row, col) => {
    return corrosionAreas.filter(area => {
      const match = area.description?.match(/\[tile_(\d+)_(\d+)\]/);
      return match && match[1] === String(row) && match[2] === String(col);
    });
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
  const handleReset = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setSelectedTile(null);
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position.x,
        posY: position.y,
      };
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      setPosition({
        x: dragStartRef.current.posX + deltaX,
        y: dragStartRef.current.posY + deltaY,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTileClick = (row, col) => {
    const tileKey = `${row}_${col}`;
    if (selectedTile === tileKey) {
      setSelectedTile(null);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setSelectedTile(tileKey);
      // Zoom into the tile
      setZoom(2.5);
      // Calculate position to center the tile
      const offsetX = (col - 1) * -150;
      const offsetY = (row - 1) * -120;
      setPosition({ x: offsetX, y: offsetY });
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  const gridCols = gridSize === 3 ? "grid-cols-3 grid-rows-3" : "grid-cols-2 grid-rows-2";

  return (
    <div className="relative" {...props}>
      {/* Controls */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-zinc-700 rounded-sm p-1">
        <Button
          onClick={() => setShowGrid(!showGrid)}
          variant="ghost"
          size="icon"
          className={`rounded-sm w-8 h-8 ${showGrid ? "text-cyan-400 bg-cyan-500/20" : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"}`}
          data-testid="toggle-grid-button"
          title="Toggle grid overlay"
        >
          <Grid3X3 className="w-4 h-4" />
        </Button>
        <div className="w-px h-4 bg-zinc-700" />
        <Button
          onClick={handleZoomOut}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-sm w-8 h-8"
          data-testid="zoom-out-button"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-xs font-mono text-zinc-400 min-w-[3rem] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          onClick={handleZoomIn}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-sm w-8 h-8"
          data-testid="zoom-in-button"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <div className="w-px h-4 bg-zinc-700" />
        <Button
          onClick={handleReset}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-sm w-8 h-8"
          data-testid="reset-zoom-button"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>

      {/* Instructions */}
      {showGrid && showOverlay && tilesWithCorrosion.size > 0 && (
        <div className="absolute top-4 left-4 z-20 bg-black/60 backdrop-blur-sm border border-zinc-700 rounded-sm px-3 py-2">
          <div className="text-xs font-mono text-cyan-400">Click red tile to zoom</div>
        </div>
      )}

      {/* Drag indicator */}
      {zoom > 1 && !selectedTile && (
        <div className="absolute top-16 left-4 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-zinc-700 rounded-sm px-3 py-1.5">
          <Move className="w-4 h-4 text-zinc-400" />
          <span className="text-xs font-mono text-zinc-400">Drag to pan</span>
        </div>
      )}

      {/* Image Container */}
      <div 
        ref={containerRef}
        className="relative min-h-[500px] bg-zinc-900 overflow-hidden cursor-move"
        onMouseDown={handleMouseDown}
        style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
      >
        <div
          className="relative w-full h-full min-h-[500px] flex items-center justify-center"
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.3s ease-out",
          }}
        >
          {image ? (
            <>
              <img
                src={image}
                alt={label}
                className="max-w-full max-h-[500px] object-contain select-none"
                draggable={false}
                data-testid="analysis-image"
              />
              
              {/* 2x2 Grid Overlay */}
              {showGrid && showOverlay && (
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    // Match the image dimensions
                    maxWidth: "100%",
                    maxHeight: "500px",
                    aspectRatio: "auto"
                  }}
                >
                  <div className={`absolute inset-0 grid ${gridCols}`}>
                    {Array.from({ length: gridSize * gridSize }).map((_, index) => {
                      const row = Math.floor(index / gridSize);
                      const col = index % gridSize;
                      const tileKey = `${row}_${col}`;
                      const hasCorrosion = tilesWithCorrosion.has(tileKey);
                      const isSelected = selectedTile === tileKey;
                      const tileDetails = getTileDetails(row, col);
                      
                      return (
                        <div
                          key={tileKey}
                          className={`
                            relative border transition-all duration-200 pointer-events-auto
                            ${hasCorrosion 
                              ? "border-red-500 bg-red-500/20 cursor-pointer hover:bg-red-500/30" 
                              : "border-zinc-600/50 hover:border-zinc-500/70"
                            }
                            ${isSelected ? "ring-2 ring-red-400 ring-inset" : ""}
                          `}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasCorrosion) {
                              handleTileClick(row, col);
                            }
                          }}
                          data-testid={`grid-tile-${row}-${col}`}
                        >
                          {/* Tile label */}
                          <div className={`
                            absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-mono rounded-sm
                            ${hasCorrosion 
                              ? "bg-red-500 text-white" 
                              : "bg-black/50 text-zinc-400"
                            }
                          `}>
                            {row},{col}
                          </div>
                          
                          {/* Corrosion indicator */}
                          {hasCorrosion && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="bg-red-500/90 text-white text-[10px] font-mono uppercase px-2 py-1 rounded-sm shadow-lg">
                                ⚠ Corrosion
                              </div>
                            </div>
                          )}
                          
                          {/* Details tooltip on hover for tiles with corrosion */}
                          {hasCorrosion && tileDetails.length > 0 && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 hover:opacity-100 transition-opacity z-30 pointer-events-none group-hover:pointer-events-auto">
                              <div className="bg-black/95 text-white text-xs px-3 py-2 rounded-sm max-w-[200px] whitespace-normal border border-red-500/50">
                                <div className="font-mono text-red-400 mb-1">Tile [{row},{col}]</div>
                                {tileDetails.map((detail, i) => (
                                  <div key={i} className="text-zinc-300">
                                    {detail.description?.replace(/\[tile_\d+_\d+\]\s*/, '')}
                                    <span className={`ml-1 text-[10px] uppercase ${
                                      detail.severity === 'high' ? 'text-red-400' :
                                      detail.severity === 'medium' ? 'text-orange-400' : 'text-yellow-400'
                                    }`}>
                                      ({detail.severity})
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-zinc-600 text-sm">No image available</div>
          )}
        </div>
      </div>

      {/* Selected Tile Details Panel */}
      {selectedTile && showOverlay && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-4 left-4 right-4 z-20 bg-black/90 backdrop-blur-sm border border-red-500/50 rounded-sm p-4"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-mono text-red-400 uppercase tracking-widest mb-1">
                Corrosion Found - Tile [{selectedTile.replace('_', ',')}]
              </div>
              <div className="text-zinc-300 text-sm">
                {getTileDetails(
                  parseInt(selectedTile.split('_')[0]), 
                  parseInt(selectedTile.split('_')[1])
                ).map((detail, i) => (
                  <div key={i} className="mb-1">
                    <span className="text-zinc-100">
                      {detail.description?.replace(/\[tile_\d+_\d+\]\s*/, '')}
                    </span>
                    <span className={`ml-2 text-xs font-mono uppercase px-1.5 py-0.5 rounded ${
                      detail.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                      detail.severity === 'medium' ? 'bg-orange-500/20 text-orange-400' : 
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {detail.severity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <Button
              onClick={() => {
                setSelectedTile(null);
                setZoom(1);
                setPosition({ x: 0, y: 0 });
              }}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white"
            >
              <Maximize2 className="w-4 h-4 mr-1" />
              Reset View
            </Button>
          </div>
        </motion.div>
      )}

      {/* Legend */}
      {showGrid && showOverlay && (
        <div className="absolute bottom-4 left-4 z-20 bg-black/60 backdrop-blur-sm border border-zinc-700 rounded-sm px-3 py-2">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-red-500/30 border border-red-500 rounded-sm" />
              <span className="text-[10px] font-mono text-zinc-400">Corrosion</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-transparent border border-zinc-600/50 rounded-sm" />
              <span className="text-[10px] font-mono text-zinc-400">Clean</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
